import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const Staff = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const userId = user?.id;

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await apiService.get('staffList');
      setStaffList(response.data || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiService.get(`deleteStaff/${deleteId}`);
      setDeleteModal(false);
      fetchStaff();
    } catch (error) {
      console.error('Error deleting staff:', error);
    } finally {
      setDeleting(false);
    }
  };

  const copyLink = () => {
    const link = `https://admin.travellinglark.com/staff/login/${userId}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 border-4 border-[#009ef7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-0">
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
        <p className="text-sm sm:text-base text-gray-600 mb-4">Share Below Details With Your Staff To Login</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={`https://admin.travellinglark.com/staff/login/${userId}`}
            readOnly
            className="flex-1 px-2 sm:px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-xs sm:text-sm"
          />
          <button onClick={copyLink} className="px-2 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg whitespace-nowrap">
            Copy Link
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h3 className="text-lg sm:text-xl font-semibold">Staff</h3>
          <Link to="/add-staff" className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 no-underline whitespace-nowrap">
            <i className="bi bi-plus-lg mr-2"></i>Add New
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Name</th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Email</th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Access</th>
                {user?.role === 'admin' && (
                  <th className="px-2 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-gray-700">Permissions</th>
                )}
                <th className="px-2 sm:px-6 py-3 text-center text-xs sm:text-sm font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {staffList.map((staff) => (
                <tr key={staff._id} className="border-t hover:bg-gray-50">
                  <td className="px-2 sm:px-6 py-4 font-medium text-gray-900 text-xs sm:text-sm">
                    {staff.first_name} {staff.last_name}
                  </td>
                  <td className="px-2 sm:px-6 py-4 text-gray-600 text-xs sm:text-sm">{staff.email}</td>
                  <td className="px-2 sm:px-6 py-4">
                    {staff.services && staff.services.length > 0 ? (
                      staff.services.map((service, idx) => (
                        <span key={idx} className="inline-block bg-[#009ef7]/10 text-[#009ef7] px-2 sm:px-3 py-1 rounded-full text-xs mr-2 mb-1">
                          {service.charAt(0).toUpperCase() + service.slice(1)}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">--</span>
                    )}
                  </td>
                  {user?.role === 'admin' && (
                    <td className="px-2 sm:px-6 py-4">
                      {staff.permissions && staff.permissions.length > 0 ? (
                        staff.permissions.map((perm, idx) => (
                          <div key={idx} className="mb-2">
                            <span className="font-semibold text-gray-900 block mb-1 text-xs sm:text-sm">
                              {perm.page.charAt(0).toUpperCase() + perm.page.slice(1)}
                            </span>
                            {perm.modules?.map((mod, midx) => (
                              <span key={midx} className="inline-block bg-cyan-100 text-cyan-700 px-2 sm:px-3 py-1 rounded-full text-xs mr-2 mb-1">
                                {mod.name}
                                {mod.actions && mod.actions.length > 0 && (
                                  <small className="font-normal"> ({mod.actions.join(', ')})</small>
                                )}
                              </span>
                            ))}
                          </div>
                        ))
                      ) : (
                        <span className="text-gray-400 italic text-xs sm:text-sm">No Permissions</span>
                      )}
                    </td>
                  )}
                  <td className="px-2 sm:px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <Link
                        to={`/edit-staff/${staff._id}`}
                        className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs sm:text-sm"
                      >
                        <i className="bi bi-pencil sm:mr-1"></i>
                      </Link>
                      <button
                        onClick={() => { setDeleteId(staff._id); setDeleteModal(true); }}
                        className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs sm:text-sm"
                      >
                        <i className="bi bi-trash sm:mr-1"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h3 className="text-lg sm:text-xl font-semibold mb-4">Confirmation</h3>
            <p className="mb-6 text-sm sm:text-base">Are you sure you want to delete this Staff?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteModal(false)} disabled={deleting} className="px-2 sm:px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm sm:text-base">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting} className="px-2 sm:px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm sm:text-base">
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Staff;
